/**
 * CART API V2 - SIMPLIFIED SHOPIFY INTEGRATION
 * Single responsibility: API calls to Shopify Cart API
 * Target: 100 lines, 8KB
 */

class CartAPIV2 {
  constructor() {
    this.baseURL = '';
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
  }

  // =============================================================================
  // CORE API METHODS
  // =============================================================================

  async getCart() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: this.defaultHeaders
      });

      if (!response.ok) {
        throw new Error(`Get cart failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Get cart failed:', error);
      throw error;
    }
  }

  async addToCart(items, options = {}) {
    try {
      const formData = new FormData();
      
      if (Array.isArray(items)) {
        // Multiple items
        items.forEach((item, index) => {
          formData.append(`items[${index}][id]`, item.id);
          formData.append(`items[${index}][quantity]`, item.quantity);
          
          if (item.properties) {
            Object.entries(item.properties).forEach(([key, value]) => {
              formData.append(`items[${index}][properties][${key}]`, value);
            });
          }
        });
      } else {
        // Single item
        formData.append('id', items.id);
        formData.append('quantity', items.quantity || 1);
        
        if (items.properties) {
          Object.entries(items.properties).forEach(([key, value]) => {
            formData.append(`properties[${key}]`, value);
          });
        }
      }

      // Bag with the add (31 Oct 2026): Shopify renders the asked section after the add and returns it in this same answer
      // (Section Rendering API), so cart-core-v2.js can take the cart from it instead of a second /cart.js request.
      if (options && options.sections) {
        formData.append('sections', options.sections);
        formData.append('sections_url', options.sectionsUrl || window.location.pathname);
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
      const answeredAt = performance.now();

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Add to cart failed: ${response.status}`);
      }

      const added = await response.json();
      // When Shopify's answer arrived: cart-core-v2.js treats the returned cart like a /cart.js read that started then.
      if (options && options.sections && added && typeof added === 'object') {
        try { Object.defineProperty(added, '__answeredAt', { value: answeredAt }); } catch (_) {}
      }
      return added;
    } catch (error) {
      console.error('❌ Add to cart failed:', error);
      throw error;
    }
  }

  async updateCart(updates) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: this.defaultHeaders,
        body: JSON.stringify({ updates })
      });

      if (!response.ok) {
        throw new Error(`Update cart failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Update cart failed:', error);
      throw error;
    }
  }

  async changeQuantity(key, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: this.defaultHeaders,
        body: JSON.stringify({ id: key, quantity })
      });

      if (!response.ok) {
        throw new Error(`Change quantity failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Change quantity failed:', error);
      throw error;
    }
  }

  async clearCart() {
    try {
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: this.defaultHeaders
      });

      if (!response.ok) {
        throw new Error(`Clear cart failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Clear cart failed:', error);
      throw error;
    }
  }

  // =============================================================================
  // SECTION RENDERING (for cart drawer updates)
  // =============================================================================

  async getSections(sectionIds) {
    try {
      const promises = sectionIds.map(async (sectionId) => {
        const response = await fetch(`/?section_id=${sectionId}`);
        if (!response.ok) {
          throw new Error(`Section ${sectionId} fetch failed: ${response.status}`);
        }
        const html = await response.text();
        return { [sectionId]: html };
      });

      const results = await Promise.all(promises);
      return Object.assign({}, ...results);
    } catch (error) {
      console.error('❌ Get sections failed:', error);
      throw error;
    }
  }
}

// =============================================================================
// INITIALIZATION
// =============================================================================

// Always initialize (V2 is default now)
console.log('🚀 Initializing Cart API V2...');
window.cartAPIV2 = new CartAPIV2();

console.log('📡 Cart API V2 module loaded');